import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useVault } from "@/context/VaultContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PasswordField } from "./PasswordField";
import { errorMessage } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Loader2, RotateCcw } from "lucide-react";

/**
 * «Start på nytt» for brukere som har mistet både hovedpassord og gjenopprettingskode.
 * Krever KONTOPASSORDET (innloggingspassordet) + at brukeren skriver SLETT. Alt kryptert innhold slettes permanent;
 * deretter vises oppsett-veiviseren slik at brukeren kan velge et nytt hovedpassord.
 */
export const ResetVaultDialog = ({ open, onOpenChange }) => {
  const { meta, resetVault } = useVault();
  const navigate = useNavigate();
  const [accountPassword, setAccountPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const count = meta?.entry_count || 0;
  const ready = accountPassword.length > 0 && confirm.trim().toUpperCase() === "SLETT";

  useEffect(() => { if (!open) { setAccountPassword(""); setConfirm(""); setError(""); } }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!ready || busy) return;
    setBusy(true);
    setError("");
    try {
      await resetVault(accountPassword, confirm);
      toast.success("Hvelvet er nullstilt. Sett opp hvelvet på nytt med et hovedpassord du velger selv.", { id: "vault-reset" });
      onOpenChange?.(false);
      navigate("/passord");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange?.(o); }}>
      <DialogContent className="bg-card sm:max-w-lg" data-testid="vault-reset-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><RotateCcw className="h-5 w-5 text-destructive" /> Nullstill hvelvet og start på nytt</DialogTitle>
          <DialogDescription>
            Hvelvet er kryptert med et hovedpassord bare du kjenner. Controlle kan ikke lese eller gjenopprette innholdet – men du kan slette alt og sette opp hvelvet på nytt.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4" noValidate>
          <Alert variant="destructive" data-testid="vault-reset-warning">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{count > 0 ? `${count} ${count === 1 ? "oppføring" : "oppføringer"} slettes for godt` : "Alt kryptert innhold slettes"}</AlertTitle>
            <AlertDescription>
              {count > 0
                ? "Passord og delinger i hvelvet kan ikke hentes tilbake etterpå. Fortsett bare hvis du er sikker på at du ikke trenger dem."
                : "Hvelvet ser tomt ut, så ingenting av verdi går tapt. Delinger du har fått fra andre må deles på nytt."}
            </AlertDescription>
          </Alert>

          <div className="space-y-1.5">
            <Label htmlFor="reset-account-pw">Kontopassordet ditt</Label>
            <PasswordField id="reset-account-pw" value={accountPassword} onChange={(e) => setAccountPassword(e.target.value)} testId="vault-reset-account-password" sensitive={false} autoComplete="current-password" />
            <p className="text-xs text-muted-foreground">Passordet du logger inn i Controlle med – ikke hovedpassordet for hvelvet.</p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="reset-confirm">Skriv <span className="font-mono font-semibold">SLETT</span> for å bekrefte</Label>
            <Input id="reset-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="SLETT" className="font-mono uppercase" autoComplete="off" spellCheck={false} data-testid="vault-reset-confirm-input" />
          </div>

          {error ? <p className="text-sm text-destructive" role="alert" data-testid="vault-reset-error">{error}</p> : null}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => onOpenChange?.(false)} disabled={busy} data-testid="vault-reset-cancel">Avbryt</Button>
            <Button type="submit" variant="destructive" disabled={!ready || busy} data-testid="vault-reset-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RotateCcw className="h-4 w-4 mr-2" />} {busy ? "Nullstiller …" : "Slett hvelvet og start på nytt"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
